import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { appSettings } from 'app/appSettings';
import { CustomerInfoSandbox } from 'app/core/sandboxes/customer-info.sandbox';
import { AzureMonitoringService } from 'app/core/services/AzureMonitoringService';
import { NotificationService } from 'app/core/services/notification.service';
import { AbstractEffects } from 'app/core/store/AbstractEffects';
import { DefinitionsService } from 'app/core/store/definitions/definitions.service';
import { RootState } from 'app/core/store/index';
import * as selectCustomerActions from 'app/core/store/session/selected-customer/select-customer.actions';
import { SetSelectedCustomerAction } from 'app/core/store/session/selected-customer/select-customer.actions';
import * as userContextActions from 'app/core/store/session/user-context/user-context.actions';
import { UserContextLoadSuccessAction } from 'app/core/store/session/user-context/user-context.actions';
import moment from 'moment';
import { CookieService } from 'ngx-cookie';
import { Observable, of } from 'rxjs';
import { filter, switchMap, withLatestFrom } from 'rxjs/operators';
import { goAction } from '../../router/router.actions';
import { SELECTED_CUSTOMER_COOKIE_NAME } from './select-customer.constants';
import { AuthenticationService } from '../../../authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class SelectCustomerEffects extends AbstractEffects {

  private authenticationService = inject(AuthenticationService);

  /**
   * when authenticated and userContext has been loaded successfully, get selectedCustomer from local cookie
   * then,
   * if superuser
   *   - clear selectedCustomer in state
   * else
   *   if selectedCustomer in cookie
   *     - set selectedCustomer in state
   *   else
   *     - go to 'select customer' page
   */
  getContractDetailsOrGotoSelectCustomer$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UserContextLoadSuccessAction>(userContextActions.USERCONTEXT_LOAD_SUCCESS),
        // debounceTime(50),
        withLatestFrom(this.authenticationService.authenticated$, this.store, (action, isAuthenticated, state) => ({
          action,
          isAuthenticated,
          state,
        })),
        filter(({ isAuthenticated }: { isAuthenticated: boolean }) => isAuthenticated),
        switchMap(({ state, action }: { state: RootState; action: UserContextLoadSuccessAction; isAuthenticated: boolean }) => {
          const selectedCustomerSessionStorage = JSON.parse(sessionStorage.getItem(SELECTED_CUSTOMER_COOKIE_NAME)) as { id: string; name: string; userId: string };
          const isSuperUser = state.session.userContext.isSuperUser;

          if (selectedCustomerSessionStorage) {
            const otherUserInSameBrowser = selectedCustomerSessionStorage && selectedCustomerSessionStorage.userId !== action.payload.id;
            return this.handleSelectedCustomerSessionStorage(selectedCustomerSessionStorage, otherUserInSameBrowser, isSuperUser);
          }

          const selectedCustomerCookie = this.cookieService.getObject(SELECTED_CUSTOMER_COOKIE_NAME) as { id: string; name: string; userId: string };
          const otherUserInSameBrowser = selectedCustomerCookie && selectedCustomerCookie.userId !== action.payload.id;
          return this.handleSelectedCustomerCookieStorage(otherUserInSameBrowser, selectedCustomerCookie, isSuperUser);
        })
      ),
    { dispatch: false }
  );

  clearSelectedCustomer$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(selectCustomerActions.CLEAR_SELECTED_CUSTOMER),
        switchMap(() => {
          this.cookieService.remove(SELECTED_CUSTOMER_COOKIE_NAME);
          sessionStorage.removeItem(SELECTED_CUSTOMER_COOKIE_NAME);
          return of(null);
        })
      ),
    { dispatch: false }
  );

  setSelectedCustomer$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SetSelectedCustomerAction>(selectCustomerActions.SET_SELECTED_CUSTOMER),
        withLatestFrom(this.store, (action, state) => ({
          action,
          state,
        })),
        switchMap(({ action, state }: { action: SetSelectedCustomerAction; state: RootState }) => {
          const in4hours = moment().add(4, 'hours');
          let customer = { ...action.payload.customer, userId: state.session.userContext.id };

          this.cookieService.putObject(SELECTED_CUSTOMER_COOKIE_NAME, customer, { expires: in4hours.toDate() });
          sessionStorage.setItem(SELECTED_CUSTOMER_COOKIE_NAME, JSON.stringify(customer));

          this.sandbox.loadCustomerInfo(action.payload.customer);

          return of(null);
        })
      ),
    { dispatch: false }
  );

  loadDefinitions$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(selectCustomerActions.SET_SELECTED_CUSTOMER),
        withLatestFrom(this.store, (action, state) => {
          return { language: state.session.userContext.language };
        }),
        switchMap(({ language }) => {
          this.definitionsService.loadOnce(language);
          return of(null);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    translateService: TranslateService,
    private cookieService: CookieService,
    private sandbox: CustomerInfoSandbox,
    private definitionsService: DefinitionsService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }

  private handleSelectedCustomerCookieStorage(otherUserInSameBrowser: boolean, selectedCustomerCookie: { id: string; name: string; userId: string; }, isSuperUser: boolean) {
    if (otherUserInSameBrowser) {
      this.clearSelectedCustomer(isSuperUser);
    }
    else if (selectedCustomerCookie && !isSuperUser) {
      this.store.dispatch(new selectCustomerActions.SetSelectedCustomerAction({ customer: selectedCustomerCookie }));
    }
    else {
      this.clearSelectedCustomer(isSuperUser);
    }

    return of(null);
  }

  private handleSelectedCustomerSessionStorage(selectedCustomerSessionStorage: { id: string; name: string; userId: string; }, otherUserInSameBrowser: boolean, isSuperUser: boolean) {
    if (otherUserInSameBrowser) {
      this.clearSelectedCustomer(isSuperUser);
    }
    else if (selectedCustomerSessionStorage && !isSuperUser) {
      this.store.dispatch(new selectCustomerActions.SetSelectedCustomerAction({ customer: selectedCustomerSessionStorage }));
    }
    else {
      this.clearSelectedCustomer(isSuperUser);
    }

    return of(null);
  }

  private clearSelectedCustomer(isSuperUser: boolean) {
    this.store.dispatch(new selectCustomerActions.ClearSelectedCustomerAction());
    if (!isSuperUser) {
      this.store.dispatch(goAction({ path: appSettings.SELECT_CUSTOMER_ROUTE }));
    }
  }
}
